.room-page {
  --content-max: 1200px;
  --container-pad: clamp(16px, 4vw, 48px);
  --tap-min: 44px;
  --section-gap: clamp(56px, 8vw, 120px);

  .page {
    width: 100%;
    min-height: 100%;
    background-color: rgba(255, 255, 255, 1);
  }

  .article-header {
    width: 100%;
  }

  // ---------------------------------------------------------------------------
  // Intro
  // ---------------------------------------------------------------------------
  .article-main {
    width: min(var(--content-max), calc(100% - 2 * var(--container-pad)));
    margin: 0 auto;
    padding: clamp(24px, 6vw, 72px) 0 clamp(64px, 10vw, 140px);
  }

  .article-back {
    display: inline-flex;
    align-items: center;
    min-height: var(--tap-min);
    color: rgba(163, 135, 95, 1);
    font-size: clamp(14px, 1.1vw, 16px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  .article-eyebrow {
    margin: clamp(20px, 3vw, 32px) 0 0;
    color: rgba(163, 135, 95, 1);
    font-size: clamp(14px, 1.2vw, 18px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .article-title {
    margin: clamp(10px, 1.6vw, 18px) 0 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(34px, 5vw, 64px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.08;
    text-transform: uppercase;
    white-space: normal;
  }

  .article-subtitle {
    margin: clamp(14px, 2vw, 20px) 0 0;
    max-width: 62ch;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(17px, 1.5vw, 22px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    line-height: 1.5;
  }

  .article-intro {
    margin: clamp(18px, 3vw, 32px) 0 0;
    max-width: 72ch;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(16px, 1.4vw, 20px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.7;
  }

  // ---------------------------------------------------------------------------
  // Hero image
  // ---------------------------------------------------------------------------
  .hero-media {
    margin: clamp(32px, 5vw, 56px) 0 0;
  }

  .hero-media__image {
    width: 100%;
    height: clamp(280px, 42vw, 560px);
    object-fit: cover;
    border-radius: 24px;
    display: block;
  }

  // ---------------------------------------------------------------------------
  // Room sections (alternating image / text)
  // ---------------------------------------------------------------------------
  .room-section {
    margin: var(--section-gap) 0 0;
    padding-top: clamp(40px, 6vw, 64px);
    border-top: 1px solid rgba(22, 0, 4, 0.1);
    display: flex;
    align-items: center;
    gap: clamp(32px, 5vw, 88px);

    &--reverse {
      flex-direction: row-reverse;
    }
  }

  .room-section__media {
    flex: 1.15 1 0;
    min-width: 0;
  }

  .room-section__image {
    width: 100%;
    height: clamp(280px, 30vw, 440px);
    object-fit: cover;
    border-radius: 24px;
    display: block;
  }

  .room-section__content {
    flex: 1 1 0;
    min-width: 0;
  }

  .room-section__status {
    margin: 0 0 0.75rem;
    font-size: 0.75rem;
    font-family: Red Rose-Medium;
    font-weight: normal;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(163, 135, 95, 1);

    &[data-tone='care'] {
      color: rgba(22, 0, 4, 0.62);
    }

    &[data-tone='evaluate'] {
      color: rgba(124, 124, 124, 1);
    }

    &[data-tone='avoid'] {
      color: rgba(22, 0, 4, 0.88);
    }
  }

  .room-section__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(24px, 2.3vw, 34px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.2;
  }

  .room-section__text {
    margin: clamp(14px, 1.8vw, 20px) 0 0;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.1vw, 18px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.7;
  }

  .room-section__list {
    margin: 1rem 0 0;
    padding-left: 1.25rem;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.05vw, 17px);
    font-family: Red Rose-Regular;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.65;
    }
  }

  // ---------------------------------------------------------------------------
  // Comparison table
  // ---------------------------------------------------------------------------
  .article-section {
    margin: var(--section-gap) 0 0;
    padding-top: clamp(32px, 5vw, 56px);
    border-top: 1px solid rgba(22, 0, 4, 0.1);
  }

  .article-section__eyebrow {
    margin: 0 0 0.75rem;
    color: rgba(163, 135, 95, 1);
    font-size: 0.75rem;
    font-family: Red Rose-Medium;
    font-weight: normal;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .article-section__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(24px, 2.3vw, 34px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.2;
  }

  .article-section__text {
    margin: clamp(12px, 1.6vw, 18px) 0 0;
    max-width: 76ch;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.1vw, 18px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    line-height: 1.7;
  }

  .article-table-wrap {
    width: 100%;
    margin-top: 2rem;
    overflow-x: auto;
    border: 1px solid rgba(22, 0, 4, 0.12);
    -webkit-overflow-scrolling: touch;
  }

  .article-table {
    width: 100%;
    min-width: 640px;
    border-collapse: collapse;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(14px, 1vw, 17px);
    font-family: Red Rose-Regular;
    text-align: left;

    th,
    td {
      padding: 1rem 1.25rem;
      border-bottom: 1px solid rgba(22, 0, 4, 0.1);
      vertical-align: top;
      line-height: 1.6;
    }

    thead th {
      color: rgba(22, 0, 4, 1);
      font-family: Red Rose-Medium;
      font-weight: normal;
      background-color: rgba(22, 0, 4, 0.035);
    }

    tbody th {
      width: 22%;
      color: rgba(22, 0, 4, 1);
      font-family: Red Rose-Medium;
      font-weight: normal;
      white-space: nowrap;
    }

    tbody td:first-of-type {
      color: rgba(22, 0, 4, 1);
      font-family: Red Rose-Medium;
      font-weight: normal;
      white-space: nowrap;
    }

    tbody tr:last-child {
      th,
      td {
        border-bottom: 0;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Considerations grid ("Think Beyond the Individual Room")
  // ---------------------------------------------------------------------------
  .considerations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0 0;
  }

  .consideration {
    padding: 1.5rem;
    border: 1px solid rgba(22, 0, 4, 0.12);
  }

  .consideration__title {
    margin: 0 0 0.6rem;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(16px, 1.2vw, 18px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    line-height: 1.35;
  }

  .consideration__text {
    margin: 0;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(14px, 1.05vw, 16px);
    font-family: Red Rose-Regular;
    line-height: 1.65;
  }

  // ---------------------------------------------------------------------------
  // Closing links
  // ---------------------------------------------------------------------------
  .article-links {
    margin: 1.75rem 0 0;
    padding-left: 1.25rem;
    color: rgba(124, 124, 124, 1);
    font-size: clamp(15px, 1.1vw, 18px);
    font-family: Red Rose-Regular;

    li {
      margin-bottom: 0.65rem;
      line-height: 1.7;
    }
  }

  .article-links__link {
    color: rgba(163, 135, 95, 1);
    text-decoration: underline;
    text-underline-offset: 3px;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  // ---------------------------------------------------------------------------
  // Breakpoints
  // ---------------------------------------------------------------------------
  @media (max-width: 900px) {
    .room-section,
    .room-section--reverse {
      flex-direction: column;
      align-items: stretch;
      gap: clamp(20px, 4vw, 32px);
    }

    .room-section__image {
      height: clamp(220px, 60vw, 340px);
      border-radius: 16px;
    }

    .hero-media__image {
      height: clamp(200px, 60vw, 320px);
      border-radius: 16px;
    }
  }

  @media (max-width: 767px) {
    .article-title {
      font-size: clamp(30px, 8vw, 40px);
    }

    .considerations-grid {
      grid-template-columns: 1fr;
    }

    .consideration {
      padding: 1.25rem;
    }

    .article-table-wrap {
      margin-top: 1.5rem;
    }

    .article-table {
      th,
      td {
        padding: 0.875rem 1rem;
      }
    }
  }
}
